import React from 'react';
import Link from 'next/link';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@cvent/blocks/hooks/useTheme';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { useTranslate } from 'nucleus-text';
import { CSSObject } from '@emotion/react';
import { Badge } from '@cvent/carina/components/Badge';
import Icon from '@components/shared/Icon';
import PageGroup, { MenuItem } from './PageGroup';
import isAbsoluteUrl from './isAbsoluteUrl';

export interface NavItem extends MenuItem {
  menuComponent?: React.ReactNode;
  url: {
    href: string;
  };
  testID?: string;
  items: NavItem[];
  order: string;
}

interface Props {
  globalNav: NavItem[];
  testID: string;
  showMobileMenu?: boolean;
  setShowMobileMenu: (value: boolean) => void;
  unreadMessageCount?: number;
  unreadReportCount?: number;
  pendingConnectionsCount?: number;
}

const useStyles = (): Record<string, CSSObject> => {
  const { font, size, backgroundColor } = useTheme();

  return {
    base: {
      marginLeft: 'auto',
      marginRight: 'auto',
      alignItems: 'center',
      justifyContent: 'center',
      maxWidth: '100%'
    },
    list: {
      display: 'flex',
      listStyle: 'none',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      '& button': {
        '& a': {
          color: font.color.base
        }
      }
    },
    listItem: {
      base: {
        height: 52,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        cursor: 'pointer',
        margin: '0 0 0 10px'
      },
      link: {
        width: '100%',
        height: '100%',
        padding: '0 12px',
        display: 'flex',
        alignItems: 'center',
        textDecoration: 'none',
        color: font.color.base,
        fontSize: font.base.size.m,
        fontWeight: font.base.weight.regular,
        '&:hover': {
          borderRadius: size.border.radius.fiveX,
          backgroundColor: backgroundColor.brand.hover
        },
        '&:focus': {
          borderRadius: size.border.radius.fiveX,
          backgroundColor: backgroundColor.brand.focus
        }
      }
    },
    mobileToggle: {
      border: 'none',
      borderRadius: 10,
      backgroundColor: 'transparent',
      position: 'relative',
      '&:hover': {
        backgroundColor: backgroundColor.brand.hover
      },
      '&:focus': {
        backgroundColor: backgroundColor.brand.focus,
        // imitating Chrome default focus outline in Firefox
        '@-moz-document url-prefix()': {
          outline: '2px solid blue',
          boxShadow: '0 0 0 3px white'
        }
      }
    },
    unreadMessageIndicator: {
      position: 'absolute',
      top: '3px',
      right: '8px',
      background: backgroundColor.danger.fill.base,
      width: '6px',
      height: '6px',
      borderRadius: '50%'
    },
    lineClamp: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      wordBreak: 'break-word',
      whiteSpace: 'initial',
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 2,
      textAlign: 'center',
      minWidth: '50px'
    },
    Badge: {
      position: 'absolute',
      left: '25px',
      top: '-1px'
    }
  };
};

function GlobalNav({
  globalNav,
  testID,
  showMobileMenu,
  setShowMobileMenu,
  unreadMessageCount,
  unreadReportCount,
  pendingConnectionsCount
}: Props): JSX.Element {
  const { font } = useTheme();
  const styles = useStyles();
  const { isMobileOrTablet, isL, isXL, isMobile, isM } = useBreakpoints();
  const { translate } = useTranslate();

  const shouldDisplayCount = !!pendingConnectionsCount || !!unreadReportCount;
  const notificationCount = pendingConnectionsCount + unreadReportCount;

  return (
    <div css={styles.base}>
      {isMobileOrTablet ? (
        <button
          type="button"
          onKeyPress={e => {
            if (e.key === 'Enter') setShowMobileMenu(!showMobileMenu);
          }}
          css={[styles.mobileToggle, styles.list]}
          {...injectTestId('global-nav-mobile-menu')}
          tabIndex={0}
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          aria-label={translate('Navigation-OpenMenu-AccessibilityLabel')}
          aria-expanded={showMobileMenu}
        >
          <Icon name="app-switcher" size="lg" color={font.color.base} aria-hidden />
          {isMobile && (!!unreadMessageCount || !!unreadReportCount) && (
            <div css={styles.unreadMessageIndicator} {...injectTestId('global-nav-unread-messages-indicator')} />
          )}
          {isM && shouldDisplayCount && (
            <Badge
              css={styles.Badge}
              content={notificationCount.toString()}
              {...injectTestId('navigation-menu-profile-badge')}
              variant="danger"
            />
          )}
        </button>
      ) : null}
      {isL || isXL ? (
        <ul css={[styles.list, { padding: 0, margin: 0 }]}>
          {globalNav?.map(navItem => {
            const titleTestId = navItem?.title.replace(/\s/g, '-').toLowerCase();
            return navItem?.items?.length === 0 ? (
              <li
                css={styles.listItem.base}
                key={`global-nav-item-${navItem.title}`}
                {...injectTestId(`global-nav-item-${titleTestId}`)}
              >
                <Link
                  href={navItem?.url?.href}
                  passHref
                  css={styles.listItem.link}
                  target={isAbsoluteUrl(navItem?.url?.href) ? '_blank' : '_self'}
                  {...injectTestId(navItem.testID)}
                  onClick={() => {
                    if (navItem?.onClick) {
                      navItem.onClick();
                    }
                  }}
                >
                  <div css={styles.lineClamp}>{navItem?.title}</div>
                </Link>
              </li>
            ) : (
              <PageGroup
                pageGroupID={testID ? `global-nav-item-${titleTestId}` : undefined}
                key={`${navItem.title}-menu`}
                navItem={navItem}
                menuItems={navItem?.items?.map(item => ({ ...item }))}
                menuComponent={navItem?.menuComponent}
              />
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

export default GlobalNav;
